import React, { useEffect, useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAppDispatch } from "../../../hooks/hooks";
import useDarkMode from "../../../hooks/useDarkMode";
import { serviceRequests } from "../../../redux/service-requests/action";

// Define the form values interface
interface FormValues {
  RequestorName: string;
  RequestorEmailId: string;
  RequestorPhoneNo: string;
  Subject: string;
  Description: string;
  ServiceRequestStatusId: number;
  ServiceRequestTypeId: number;
}

export const ServiceRequest: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { register, handleSubmit, clearErrors, formState: { errors }, setValue } = useForm<FormValues>();
  const [baseURL, setBaseUrl] = useState<string>('');
  const [colorTheme, setTheme] = useDarkMode();
  const [darkSide, setDarkSide] = useState<boolean>(colorTheme === "light" ? true : false);

  useEffect(() => {
    setBaseUrl(window.location.origin);
    clearErrors();
  }, [clearErrors]);

  const onFinish: SubmitHandler<FormValues> = async (data) => {
    dispatch(serviceRequests.update({
      url: `https://api.digiit.${process.env.REACT_APP_API_URL}/api/v3.0/service-desk/service-request`,
      entity: 'serviceRequest',
      jsonData: {
        Records: [
            {...data,
            "ServiceRequestStatusId": 10,
            "ServiceRequestTypeId": 10
            }
        ]
      }
    })).then(() => {
        toast.success('Request submitted successfully. We will get back to you as soon as possible.')
      navigate('/'); // Navigate or any other action on success
    }).catch((error: any) => {
      console.error('Error:', error);
    });
  };

  const toggleDarkMode = (checked: boolean) => {
    setTheme(colorTheme);
    setDarkSide(checked);
  };

  return (
    <form onSubmit={handleSubmit(onFinish)}>
      <div className="flex-1">
        <div className="bg-white sm:p-10 p-5">
          <div className="title flex flex-col items-center">
            <h1 className="lg:text-[34px] text-xl font-bold text-qblack">Have Any Question</h1>
            <span className="-mt-2 block">
              <svg
                width="354"
                height="30"
                viewBox="0 0 354 30"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1 28.8027C17.6508 20.3626 63.9476 8.17089 113.509 17.8802C166.729 28.3062 341.329 42.704 353 1"
                  stroke="#FFBB38"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </span>
          </div>
          <div className="inputs mt-5">
          <h6 className="input-label text-qgray capitalize text-[13px] font-normal block mb-2">Name*</h6>

            <div className="mb-4 input-wrapper border border-qgray-border w-full h-full overflow-hidden relative">
              <input
                placeholder="Your Name"
                {...register('RequestorName', { required: "Name is required", minLength: { value: 3, message: "Minimum length is 3" }, maxLength: { value: 100, message: "Maximum length is 100" } })}
                type="text"
                className={`input-field !h-[50px] placeholder:text-sm text-sm px-6 text-dark-gray w-full h-full font-normal bg-white focus:ring-0 focus:outline-none`}
              />
              {errors.RequestorName && <p className="error">{errors.RequestorName.message}</p>}
            </div>
            <h6 className="input-label text-qgray capitalize text-[13px] font-normal block mb-2">Phone Number*</h6>

            <div className="mb-4 input-wrapper border border-qgray-border w-full h-full overflow-hidden relative">

              <input
                placeholder="Your Phone Number"
                {...register('RequestorPhoneNo', {
                  required: "Phone number is required",
                  maxLength: { value: 15, message: "Maximum length is 15" },
                  minLength: { value: 10, message: "Minimum length is 10" },
                  pattern: { value: /^[0-9+-]+$/, message: "Invalid phone number" }
                })}
                type="text"
                className={`input-field !h-[50px] placeholder:text-sm text-sm px-6 text-dark-gray w-full h-full font-normal bg-white focus:ring-0 focus:outline-none`}

              />
              {errors.RequestorPhoneNo && <p className="error">{errors.RequestorPhoneNo.message}</p>}
            </div>
            <h6 className="input-label text-qgray capitalize text-[13px] font-normal block mb-2">Email Address*</h6>

            <div className="mb-4 input-wrapper border border-qgray-border w-full h-full overflow-hidden relative">

              <input
                placeholder="Your email address"
                {...register('RequestorEmailId', {
                  required: "Email is required",
                  pattern: {
                    value: /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                    message: "Invalid email address"
                  }
                })}
                type="text"
                className={`input-field !h-[50px] placeholder:text-sm text-sm px-6 text-dark-gray w-full h-full font-normal bg-white focus:ring-0 focus:outline-none`}

              />
              {errors.RequestorEmailId && <p className="error">{errors.RequestorEmailId.message}</p>}
            </div>
            <h6 className="input-label text-qgray capitalize text-[13px] font-normal block mb-2">Subject*</h6>

            <div className="mb-4 input-wrapper border border-qgray-border w-full h-full overflow-hidden relative">

              <input
                placeholder="Subject"
                {...register('Subject', { required: "Subject is required", minLength: { value: 3, message: "Minimum length is 3" }, maxLength: { value: 150, message: "Maximum length is 150" } })}
                type="text"
                className={`input-field !h-[50px] placeholder:text-sm text-sm px-6 text-dark-gray w-full h-full font-normal bg-white focus:ring-0 focus:outline-none`}
              />
              {errors.Subject && <p className="error">{errors.Subject.message}</p>}
            </div>
            <div className="mb-5">
              <h6 className="input-label text-qgray capitalize text-[13px] font-normal block mb-2">Message*</h6>
              <textarea
                placeholder="Type your message here"
                {...register('Description', { required: "Description is required", minLength: { value: 3, message: "Minimum length is 3" } })}
                className="w-full h-[105px] focus:ring-0 focus:outline-none p-3 border border-qgray-border placeholder:text-sm"
              ></textarea>
              {errors.Description && <p className="error">{errors.Description.message}</p>}
            </div>
            <div>
              <button
                type="submit"
                className="bg-black text-white text-xl p-2 w-full"
              
              >Send Now</button>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};
